import { EMEARegion, LARegion, NARegion } from "@dana/Common/Constants";
import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import useIsNA from "@dana/Common/Hooks/useIsNA";
import useWindowSize from "@dana/Common/Hooks/useWindowSize";
import { filterPageTypeByUserRoles } from "@dana/Common/NavigationHelpers";
import CheckAvailabilityProductSelector from "@dana/Components/CheckAvailability/CheckAvailabilityProductSelector";
import CustomerScoreCard from "@dana/Components/CustomerScoreCard/CustomerScoreCard";
import DrawerHamburger, { DrawerPresentationProps } from "@dana/Components/Drawer/DrawerHamburger";
import CrossIcon from "@dana/Icons/CrossIcon";
import Menu from "@dana/MobiusOverrides/Icons/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@dana/MobiusOverrides/PanelMenu/PanelMenu";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { LanguageOption } from "@dana/Overrides/Widgets/Common/LanguageMenu";
import { iconMap } from "@dana/Overrides/Widgets/Header/MainNavigation";

import {
    checkCustomerScorecard,
    checkLeadTimes,
    checkPriceList,
    DanaCoopData,
    DanaCoreReturnsData,
    downloadLeadTimes,
    downloadPriceList,
    getCoopToken,
    getCoreReturnsToken,
    getCustomerScorecard,
} from "@dana/Services/DanaMenuService";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import { SoldToAddress } from "@dana/Types/ApiModels";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LanguageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { MappedLink } from "@insite/mobius/Menu";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const supportPage = getPageLinkByPageType(state, "DanaCustomerSupportPage");
    const billtoCustomer = getBillToState(state, state.context.session.billToId);
    const quickOrderUrl = getPageLinkByPageType(state, "QuickOrderPage")?.url;
    let addressLabel = "";
    let nameLabel = "";
    const { isAuthenticated, rememberMe, isGuest, shipToId, properties } = state.context.session;
    const userRType = properties?.userType;
    const shipToState = getShipToState(state, shipToId);
    const { value: shipTo } = shipToState;
    if ((isAuthenticated || rememberMe) && !isGuest && shipTo) {
        addressLabel = shipTo.customerNumber;
        nameLabel = shipTo.firstName;
    }
    const myAccountLink = getPageLinkByPageType(state, "MyAccountPage");
    const untrimedUserRoles = state.context.session?.userRoles?.split(",") ?? [];
    const userRoles = untrimedUserRoles.map(x => x.trim());
    const userType = state.context.session?.properties?.userType ?? "";
    const session = state.context.session;
    const danaSettings = getDanaGeneralSettings(state);
    const languages = getLanguages(state);
    const currentLanguage = state.context.session.language;
    const isEmea = useIsEmea(state);
    // const selectedLanguage = languages?.find(l => l.id === currentLanguage?.id);
    const isEMEA = useIsEmea(state);
    const isNARegion = useIsNA(state);
    const soldto =
        session?.properties?.soldToAddress &&
            session?.properties?.soldToAddress !== "" &&
            session?.properties?.soldToAddress !== undefined
            ? (JSON.parse(session?.properties?.soldToAddress) as SoldToAddress)
            : undefined;

    const canSeeSupportLink = session.isAuthenticated && !session.isGuest && userType !== "ERFQ";

    let menuItems = (myAccountLink?.children?.filter((pageLink: any) => !(pageLink.type === "ChangeCustomerPage")) ??
        []) as MappedLink[];

    menuItems = filterPageTypeByUserRoles(menuItems, userRoles?.map(x => x.trim()) ?? []);

    const isPriceListEnabled = danaSettings?.isPriceListEnabled;
    const mapPriceListFileName = danaSettings?.mapPriceListFileName;
    const isPriceListEnabledOnlyForInternal = danaSettings?.isFuturePriceListEnabledForInternalUserOnly;

    const priceListItems: MappedLink[] = [
        { title: translate("Current Price List"), url: "/MyAccount/DownloadPriceList/CurrentPriceList" },
        ...(isPriceListEnabled &&
            (!isPriceListEnabledOnlyForInternal || userType === "Internal")
            ? [{ title: translate("Future Price List"), url: "/MyAccount/DownloadPriceList/FuturePriceList" }]
            : []),
    ];

    const settingsItem: MappedLink = {
        title: translate("Settings"),
        children: [{ title: translate("PriceToggle"), url: "" }],
        url: "",
    } as any;

    const conditionalMenuItems: { title: string; replacement: MappedLink; condition: boolean }[] = [
        {
            title: translate("Overstock"),
            replacement: { title: translate("Overstock Sale"), url: "/overstock" },
            condition: isNARegion && isAuthenticated,

        },
        {
            title: translate("Download Price List"),
            replacement: { title: translate("Download Price List"), url: "/MyAccount/DownloadPriceList", children: priceListItems },
            condition: userRoles.includes("Dana_DownloadPriceFile"),
        },

        {
            title: translate("Download Lead Times"),
            replacement: { title: translate("Download Lead Times"), url: "/MyAccount/DownloadLeadTimes" },
            condition: isAuthenticated,
        },
        {
            title: translate("Core Return"),
            replacement: { title: translate("Core Return"), url: "/MyAccount/CoreReturn" },
            condition: userRoles.includes("Dana_CoreReturn") && !isEMEA,
        },
        {
            title: translate("API Developer"),
            replacement: {
                title: translate("API Developer"),
                url: "/MyAccount/APIDeveloper",
                children: [
                    { title: translate("Non-Prod Portal"), url: danaSettings?.apimNonProdAPIPortal, openInNewWindow: true },
                    { title: translate("Prod Portal"), url: danaSettings?.apimProdAPIPortal, openInNewWindow: true },
                    { title: translate("API Key"), url: "/MyAccount/APIDeveloper/apikey" },
                ],
            },
            condition: userRoles.includes("Dana_Developer") && !isEMEA,
        },
        {
            title: translate("Co Op"),
            replacement: { title: translate("Co Op"), url: "/MyAccount/CoOp" },
            condition: userRoles.includes("Dana_Co-op") && !isEMEA,
        },
        {
            title: translate("Order Literature"),
            replacement: { title: translate("Order Literature"), url: danaSettings?.orderLiteratureLink },
            condition: userRoles.includes("Dana_OrderLiterature") && !isEMEA,
        },
        {
            title: translate("Tradeshows"),
            replacement: { title: translate("Tradeshows"), url: danaSettings?.tradeshowsLink },
            condition: userRoles.includes("Dana_Tradeshows") && isNARegion && isAuthenticated,
        },
        {
            title: translate("Settings"),
            replacement: settingsItem,
            condition: userType === "Internal" || (userType === "Customer" && userRoles && userRoles.length > 0 && userRoles.map(x => x.trim()).includes("Dana_SeePrice")),
        },
    ];

    conditionalMenuItems.forEach(({ title, replacement, condition }) => {
        const index = menuItems.findIndex(item => item.title === title);
        if (index !== -1) {
            if (condition) {
                menuItems = [...menuItems.slice(0, index), replacement, ...menuItems.slice(index + 1)];
            } else {
                menuItems = [...menuItems.slice(0, index), ...menuItems.slice(index + 1)];
            }
        } else if (condition) {
            menuItems = [...menuItems, replacement];
        }
    });

    return {
        billToId: session?.billToId,
        soldto,
        menuItems,
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages,
        currentLanguage,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: myAccountLink,
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: `${translate("Customer No.")} ${addressLabel} ${nameLabel ? ` - ${nameLabel}` : ''}`,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        createAccountUrl: createAccountPageLink,
        billtoCustomer,
        quickOrderUrl,
        userRoleType: userRType,
        canSeeSupportLink,
        supportPage,
        salesRep: session?.properties?.salesRepDetails ? JSON.parse(session.properties.salesRepDetails) : undefined,
        isEmea
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    downloadLeadTimes,
    downloadPriceList,
};

interface OwnProps {
    widgetId: string;
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    drawerSectionWrapperMyAccount?: InjectableCss;
    checkAvailabilityButtonMobileWrapper?: InjectableCss;
    quickOrderMobileButtonWrapper?: InjectableCss;
    customerNumberSection?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelMenuMyaccount?: PanelMenuPresentationProps;
    searchByVehiclePanelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowBrowseProductHam?: PanelRowPresentationProps;
    mainNavigationRowHamburger?: PanelRowPresentationProps;
    languageSelectorMobileRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    mainNavigationRowTypographyHamburgerLogin?: TypographyPresentationProps;
    mainNavigationRowTypographyLanguage?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    customerNumberRow?: PanelRowPresentationProps;
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    accordionSectionHambuger?: AccordionSectionPresentationProps;
    checkAvailabilityButtonMobile?: ButtonPresentationProps;
    quickOrderMobileButton?: ClickablePresentationProps;
    loginLinkClickable?: ClickablePresentationProps;
    checkAvailabilityModalMobile?: ModalPresentationProps;
    languageSelectorWrapperMobile?: InjectableCss;
    modal?: ModalPresentationProps;
    form?: InjectableCss;
    container?: GridContainerProps;
    modalGridItem?: GridItemProps;
    h2?: TypographyPresentationProps;
    key?: TypographyPresentationProps;
    generateButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    salesRepwrapper?: InjectableCss;
    salesRepDetails?: InjectableCss;
    salesRepName?: TypographyPresentationProps;
    salesRepEmail?: LinkPresentationProps;
    salesRepContact?: LinkPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const DropdownMenu = styled.ul`
    color: ${themeColors.primary.richBlack};
    z-index: 1000;
    list-style: none;
    margin: 0;
    min-width: 100%;
    flex-basis: 100%;
    gap: 8px;
    width: 100%;
    padding-left: 0;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
`;

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "rectangle",
        size: 43,
        css: css`
            display: flex;
            width: 24px;
            height: 16px;
            background: ${themeColors.primary.richBlack};
            [class*="IconWrapper"] {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaBrightBlue};
                    }
                }
            }
            &:focus {
                outline: none;
            }
            &:hover {
                background: transparent;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: none;
                            background: none;
                            justify-content: center;
                            padding: 0px;
                            min-height: 25px;
                        `,
                        css`
                            border: none;
                            background: none;
                            justify-content: center;
                            padding: 0px;
                            min-height: 25px;
                        `,
                        css`
                            border: none;
                            background: none;
                            justify-content: center;
                            padding: 0px;
                            min-height: 25px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    drawer: {
        size: 400,
        cssOverrides: {
            scrim: css`
                top: var(--header-nav-height);

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                top: 152px;
                            `,
                            css`
                                top: 152px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            drawerBody: css`
                top: var(--header-nav-height);
                .sample.header-sample {
                    &:empty {
                        padding: 0;
                        margin-bottom: -10px;
                    }
                }
                .drawer-next-button {
                    width: 16px;
                    height: 16px;
                    color: ${themeColors.primary.richBlack};
                    margin-left: auto;
                    svg {
                        width: 16px;
                        height: 16px;
                        path {
                            fill: ${themeColors.primary.richBlack};
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: 100%;
                                top: 152px;
                            `,
                            css`
                                top: 152px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
            `,
            titleButton: css`
                background: none;
                border: none;
                padding: 0;
                span {
                    svg {
                        color: ${themeColors.primary.richBlack};
                    }
                }

                &:hover {
                    border: none;
                    background: transparent;
                }
            `,
        },
        closeButtonIconProps: {
            src: X,
        },
    },
    drawerSectionWrapper: {
        css: css`
            top: var(--header-nav-height);
        `,
    },

    drawerSectionWrapperMyAccount: {
        css: css`
            .main-navigation-row {
                background: ${themeBackgrounds.white};
            }
        `,
    },

    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            &.on-mobile-globe {
                svg {
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "capitalize",
        color: themeColors.primary.richBlack,
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "common.background",
    },
    accordionSectionHambuger: {
        headerProps: {
            css: css`
                button {
                    display: flex;
                    padding: 8px 16px;
                    justify-content: space-between;
                    background: ${themeColors.primary.white};
                    align-items: center;
                    align-self: stretch;

                    :hover {
                        background: ${themeColors.primary.white};
                    }
                }
                button[aria-expanded="true"] {
                    display: flex;
                    padding: 8px 16px;
                    justify-content: space-between;
                    background: ${themeColors.primary.white};
                    align-items: center;
                    align-self: stretch;
                }
                button:focus {
                    display: flex;
                    padding: 8px 16px;
                    justify-content: space-between;
                    background: white;
                    align-items: center;
                    align-self: stretch;
                    outline-offset: -1px;
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 0px;
                border: 0;

                > div > div:nth-child(2) {
                    h3 {
                        display: none;
                    }
                }
            `,
        },
    },
    mainNavigationRow: {
        color: themeColors.primary.richBlack,
        css: css`
            &.signIn {
                background: ${themeColors.primary.danaBlue};
            }
            width: 100%;
            display: flex;
            padding: 8px 16px;
            justify-content: space-between;
            background: ${themeColors.primary.white};
            align-items: center;
            align-self: stretch;

            .drawer-next-button {
                width: 16px;
                height: 16px;
                color: ${themeColors.primary.richBlack};
                margin-left: auto;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    mainNavigationRowBrowseProductHam: {
        color: themeColors.primary.richBlack,
        css: css`
            width: 100%;
            display: flex;
            padding: 8px 16px;
            justify-content: start;
            background: ${themeColors.primary.white};
            gap: 24px;
            align-items: center;
            align-self: stretch;

            .drawer-next-button {
                width: 16px;
                height: 16px;
                color: ${themeColors.primary.richBlack};
                margin-left: auto;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    mainNavigationRowHamburger: {
        css: css`
            background: ${themeColors.primary.danaBlue};
            justify-content: start;
            padding: 8px 16px;

            .drawer-next-button {
                width: 16px;
                height: 16px;
                color: ${themeColors.primary.white};
                margin-left: auto;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.white};
                    }
                }
            }
        `,
    },

    languageSelectorMobileRow: {
        color: themeColors.primary.richBlack,

        css: css`
            background: ${themeBackgrounds.lightGray};
            justify-content: flex-start;
            height: 40px;
            padding: 8px 16px;
            .drawer-next-button {
                width: 16px;
                height: 16px;
                color: ${themeColors.primary.richBlack};
                margin-left: auto;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    searchByVehiclePanelMenu: {
        cssOverrides: {
            menu: css`
                .global-search-main-container {
                    padding: 0px;

                    dd {
                        padding: 16px;
                    }

                    dt {
                        button {
                            .toggle {
                                width: 16px;
                                height: 16px;
                                color: ${themeColors.primary.richBlack};
                                margin-left: auto;
                                svg {
                                    width: 16px;
                                    height: 16px;
                                    path {
                                        fill: ${themeColors.primary.richBlack};
                                    }
                                }
                            }
                        }
                    }
                }
            `,
        },
    },

    panelMenu: {
        cssOverrides: {
            wrapper: css`
                text-align: left;
                > a,
                button {
                    border-bottom: 1px solid ${themeBackgrounds.lightGray};
                }
            `,
            menu: css``,
            children: css`
                > .mobile-menu-header {
                    padding: 8px 16px;
                    height: 40px;
                    align-items: center;

                    .back-button {
                        padding: 0px;
                        span[class*="IconWrapper"] {
                            align-items: center;
                            color: ${themeColors.primary.richBlack};
                            font-size: ${fontSizes.medium};
                            line-height: ${lineHeights.medium};
                            font-weight: ${fontWeights.regular};
                        }

                        span:not([class*="ButtonIcon"]) {
                            color: ${themeColors.primary.richBlack};
                            margin-top: 2px;
                            font-size: ${fontSizes.medium};
                            line-height: ${lineHeights.medium};
                            font-weight: ${fontWeights.regular};
                        }
                    }

                    .close-button {
                        padding: 0px;
                        align-items: center;
                        > span {
                            align-items: center;
                            font-size: ${fontSizes.medium};
                            line-height: ${lineHeights.medium};
                            font-weight: ${fontWeights.regular};
                        }
                    }
                }

            `,
        },
        childTypographyProps: {
            transform: "capitalize",
        },
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "capitalize",
        color: themeColors.primary.richBlack,
        css: css`
            background: ${themeColors.primary.white};
        `,
    },
    mainNavigationRowTypographyHamburgerLogin: {
        color: themeColors.primary.white,
        css: css`
            background: transparent;
        `,
    },
    mainNavigationRowTypographyLanguage: {
        transform: "uppercase",
        color: themeColors.primary.richBlack,
        css: css`
            background: transparent;
        `,
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "capitalize",

            css: css`
                &.mobile-logo-title {
                    text-transform: uppercase;
                }
            `,
        },
        css: css`
            &.mobilePanel {
                background: ${themeBackgrounds.lightGray};
            }

            display: flex;
            height: 40px;
            padding: 8px 16px;
            background: ${themeBackgrounds.white};
            justify-content: space-between;
            align-items: center;
            flex-shrink: 0;
            align-self: stretch;
            border-bottom: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            padding: 16px;
            background: transparent;
            &:focus {
                outline: none;
            }
        `,
    },
    customerNumberRow: {
        css: css`
            width: 100%;
            display: flex;
            padding: 8px 16px;
            justify-content: space-between;
            background: ${themeColors.primary.white};
            align-items: center;
            align-self: stretch;
            font-size: 1rem;
            line-height: 1.5rem;
            .drawer-next-button {
                width: 16px;
                height: 16px;
                color: ${themeColors.primary.richBlack};
                margin-left: auto;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 0 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: {
        width: 12,
    },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRowText: {
        transform: "capitalize",
        color: themeColors.primary.white,
    },
    checkAvailabilityButtonMobile: {
        variant: "tertiary",
        mergeCss: true,
        css: css`
            display: flex;
            align-items: center;
            padding: 8px 16px;
            width: 100%;
            backface-visibility: visible;
        `,
        typographyProps: {
            color: themeColors.primary.richBlack,
            underline: false,
            size: fontSizes.medium,
            lineHeight: lineHeights.defaultLarge,
            weight: fontWeights.regular,
        },
    },
    quickOrderMobileButton: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px 16px;
            width: 100%;
            background: none;
            color: ${themeColors.primary.richBlack};
            font-size: ${fontSizes.medium};
            line-height: ${lineHeights.medium};
            font-weight: ${fontWeights.regular};
        `,
    },
    loginLinkClickable: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px 16px;
            width: 100%;
            background: ${themeColors.primary.danaBlue};
            color: ${themeColors.primary.white};
            font-size: ${fontSizes.medium};
            line-height: ${lineHeights.medium};
            font-weight: ${fontWeights.regular};
            border-bottom: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    checkAvailabilityModalMobile: {
        sizeVariant: "large",
    },
    checkAvailabilityButtonMobileWrapper: {
        css: css`
            border-bottom: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    quickOrderMobileButtonWrapper: {
        css: css`
            border-bottom: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    customerNumberSection: {
        css: css`
            display: flex;
            height: 40px;
            padding: 8px 16px;
            justify-content: space-between;
            align-items: center;
            align-self: stretch;
            border-bottom: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    signOutRow: {
        css: css`
            background: ${themeColors.primary.danaBlue};
            padding: 8px 16px;
            width: 100%;
        `,
    },

    // Style for Language selector Mobile
    languageSelectorWrapperMobile: {
        css: css`
            text-align: left;
            padding: 8px 16px;
        `,
    },
    modal: {
        size: 768,
    },
    container: { gap: 15 },
    modalGridItem: {
        css: css`
            justify-content: flex-center;
        `,
        width: 12,
    },
    h2: {
        variant: "h2",
        css: css`
            font-size: 1.1rem;
            font-weight: 500;
            margin-right: 1rem;
            line-height: 1.5rem;
        `,
    },
    key: {
        variant: "p",
        css: css`
            line-height: 1.5rem;
        `,
    },
    generateButton: { variant: "primary" },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    form: {
        css: css`
            display: none;
        `,
    },
    salesRepwrapper: {
        css: css`
            position: relative;
            width: 100%;
            background-size: cover;
            background-position: center;
            border-radius: 8px;
            cursor: pointer;
        `,
    },
    salesRepDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    salesRepName: {
        weight: fontWeights.semiBold,
        css: css`
            margin-bottom: 4px;
        `,
    },
    salesRepEmail: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    salesRepContact: {
        css: css`
            margin-bottom: 4px;
        `,
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawerHamburgerMenu = ({
    widgetId,
    links,
    displayVmiNavigation,
    displayModeSwitch,
    currentMode,
    isCompactHeaderFlyout,
    showFlyoutPricingToggle,
    showFlyoutInventoryToggle,
    rootElementSelector,
    languages,
    currentLanguage,
    currentLocation,
    userName,
    isGuest,
    signInUrl,
    vmiDashboardPageUrl,
    homePageUrl,
    headerLinkListLinkFields,
    fulfillmentLabel,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    isPunchOutSession,
    cart,
    history,
    theme,
    displayPricingAndInventoryToggles,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    visibilityState,
    allowOpenParentLinks,
    createAccountUrl,
    myAccountPageLink,
    quickOrderUrl,
    userRoleType,
    menuItems,
    soldto,
    billToId,
    downloadLeadTimes,
    downloadPriceList,
    canSeeSupportLink,
    supportPage,
    salesRep,
    isEmea
}: Props) => {
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";
    const [availabilityModalIsOpen, setAvailabilityModalIsOpen] = useState(false);
    const [myAccountPageLinks, setMyAccountPageLinks] = useState<Readonly<PageLinkModel> | undefined>(
        myAccountPageLink,
    );
    const [menuItemLinks, setMenuItemLinks] = useState<MappedLink[]>([]);

    const [modalType, setModalType] = useState<string>("");

    const buttonClickHandler = () => {
        setAvailabilityModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setAvailabilityModalIsOpen(false);
    };

    const headerLinkListLinks = useGetLinks(headerLinkListLinkFields, o => o.fields.destination);
    const openDrawerHamburgerMenu = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const [hasGenerated, setHasGenerated] = useState<boolean | undefined>(false);
    const [isLoading, setIsLoading] = useState<boolean | undefined>(false);
    const [isApiKeyOpen, setIsApiKeyOpen] = useState<boolean | undefined>(false);
    const [isCoreReturnsLoading, setIsCoreReturnsLoading] = useState<boolean | undefined>(false);
    const [isPriceListLoading, setIsPriceListLoading] = useState<boolean | undefined>(false);
    const [isLeadTimesLoading, setIsLeadTimesLoading] = useState<boolean | undefined>(false);
    const [isCoopLoading, setIsCoopLoading] = useState<boolean | undefined>(false);
    const [isScorecardLoading, setIsScorecardLoading] = useState<boolean | undefined>(false);
    const [scorecardUrl, setSorecardUrl] = useState<string | undefined>(undefined);
    const [apiKey, setApiKey] = useState<string | undefined>("");
    const [openScoreCardModal, setopenScoreCardModal] = useState<boolean>(false);
    const [openSalesRepModal, setOpenSalesRepModal] = useState<boolean>(false);
    const [openPriceListModal, setOpenPriceListModal] = useState<boolean>(false);
    const [openCoreReturnstModal, setOpenCoreReturnsModal] = useState<boolean>(false);
    const [openCoopModal, setOpenCoopModal] = useState<boolean>(false);
    const [openLeadTimesModal, setOpenLeadTimesModal] = useState<boolean>(false);
    const [isFuture, setIsFuture] = useState<boolean>(false);
    const [coreReturnsData, setCoreReturnsData] = useState<DanaCoreReturnsData | undefined>(undefined);
    const [coopData, setCoopData] = useState<DanaCoopData | undefined>(undefined);
    const coreReturnsFormRef = useRef<HTMLFormElement>(null);
    const coopFormRef = useRef<HTMLFormElement>(null);
    const url = IS_SERVER_SIDE ? getUrl()! : window.location;
    const [leadTimeRequestId, setLeadTimeRequestId] = useState<string | undefined>(undefined);
    const [priceListRequestId, setPriceListRequestId] = useState<string | undefined>(undefined);
    const [scorecardRequestId, setScorecardRequestId] = useState<string | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    let leadTimeIntervalId: NodeJS.Timeout | undefined;
    let priceListIntervalId: NodeJS.Timeout | undefined;
    let scorecardIntervalId: NodeJS.Timeout | undefined;
    const toasterContext = useContext(ToasterContext);
    useEffect(() => {
        setMenuItemLinks(menuItems)
    }, [menuItems]);
    useEffect(() => {

        if (leadTimeRequestId) {
            leadTimeIntervalId = setInterval(() => {
                checkLeadTimes(leadTimeRequestId, soldto?.CustomerSequence ?? "").then(done => {
                    if (done && leadTimeIntervalId) {
                        clearInterval(leadTimeIntervalId);
                        setLeadTimeRequestId(undefined);
                        setOpenLeadTimesModal(false);
                        setIsLeadTimesLoading(false);
                    }
                });
            }, 20000);
        } else if (priceListRequestId) {
            priceListIntervalId = setInterval(() => {
                checkPriceList(priceListRequestId, soldto?.CustomerSequence ?? "").then(done => {
                    if (done && priceListIntervalId) {
                        clearInterval(priceListIntervalId);
                        setPriceListRequestId(undefined);
                        setOpenPriceListModal(false);
                        setIsPriceListLoading(false);
                    }
                });
            }, 20000);
        } else if (scorecardRequestId) {
            scorecardIntervalId = setInterval(() => {
                checkCustomerScorecard(scorecardRequestId, soldto?.CustomerSequence ?? "").then(done => {
                    if (done) {
                        clearInterval(scorecardIntervalId);
                        setScorecardRequestId(undefined);
                        setSorecardUrl(done);
                        setIsScorecardLoading(false);
                    }
                });
            }, 20000);
        } else if (coreReturnsData && coreReturnsFormRef?.current) {
            coreReturnsFormRef.current.submit();
        } else if (coopData && coopFormRef?.current) {
            coopFormRef.current.submit();
        }
    }, [leadTimeRequestId, priceListRequestId, scorecardRequestId, scorecardUrl, coreReturnsData, coopData]);

    const closeApiKeyModalHandler = () => {
        setIsApiKeyOpen(false);
    };

    const handleCoreReturns = () => {
        setOpenCoreReturnsModal(true);
        setIsCoreReturnsLoading(true);
        getCoreReturnsToken().then((response: DanaCoreReturnsData | undefined) => {
            if (response) {
                setCoreReturnsData(response);
            }
        });
    };

    const handleDownloadLeadTimes = () => {
        setOpenLeadTimesModal(true);
        setIsLeadTimesLoading(true);
        toasterContext.addToast({
            body: translate("Published Lead time download has started."),
            messageType: "success",
            isFocusAfterMount: false,
        });
        downloadLeadTimes().then(requestid => {
            if (requestid) {
                setLeadTimeRequestId(requestid);
            }
        });
    };

    const handleDownloadPriceList = (isFuture: boolean) => {
        setOpenPriceListModal(true);
        setIsFuture(isFuture);
    };

    const handleCloseDownloadPriceList = () => {
        setOpenPriceListModal(false);
    };

    const handleOpenScoreCardModal = (modalType: string) => {
        setIsScorecardLoading(true);
        setopenScoreCardModal(true);
        setModalType(modalType);
        getCustomerScorecard().then(requestId => {
            if (requestId) {
                setScorecardRequestId(requestId);
            }
        });
    };

    const closeScoreCardModal = () => {
        setopenScoreCardModal(false);
        setModalType("");
    };

    const handleFindMyRep = () => {
        setOpenSalesRepModal(true);
    };

    const closeSalesRepModal = () => {
        setOpenSalesRepModal(false);
    };

    const getGenApiKey = async () => {
        setIsLoading(true);
        let apiKeyResult = await fetch(`/api/v1/dana/generateKey?billToId=${billToId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "text/plain",
            },
            body: JSON.stringify({ billToId }),
        });
        if (apiKeyResult.ok) {
            let apiKeyString = await apiKeyResult.text();
            if (apiKeyString !== undefined && apiKeyString !== "") {
                setHasGenerated(true);
                setApiKey(apiKeyString.replace(new RegExp('\\"', "g"), ""));
            }
        } else {
            setErrorMessage("Could not generate new API Key for current user.");
        }
        setIsLoading(false);
    };

    const getExistingKey = async () => {
        if (billToId !== undefined) {
            let apiKeyResult = await fetch(`/api/v1/dana/getApiKey?billToId=${billToId}`, {
                method: "GET",
                headers: {
                    Accept: "text/plain",
                },
            });
            if (apiKeyResult.ok) {
                let apiKeyString = await apiKeyResult.text();
                if (apiKeyString !== undefined && apiKeyString !== "") {
                    setHasGenerated(true);
                    setApiKey(apiKeyString.replace(new RegExp('\\"', "g"), ""));
                    setIsLoading(false);
                    return true;
                }
            }
            setIsLoading(false);
            return false;
        }
    };

    const handleApiKey = () => {
        setIsApiKeyOpen(true);
        getExistingKey().then(() => { });
    };

    const generateKey = () => {
        getGenApiKey();
    };

    const scoreCardModalTitle =
        modalType === "Single Account"
            ? translate("Customer Scorecard: Single Account")
            : translate("Customer Scorecard: Buying Group");

    const closeDrawerHamburgerMenu = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const signInPanelClickHandler = (event: React.MouseEvent) => {
        if (theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }

        closeDrawerHamburgerMenu();
    };

    const createAccountHandler = () => {
        closeDrawerHamburgerMenu();
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl?.url);
    };

    const signInPanelKeyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === " " && history && signInUrl?.url) {
            event.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                history.push(signInUrl.url);
            }

            closeDrawerHamburgerMenu();
        }
    };

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    homePageUrl && history.push(homePageUrl);
                },
            });
        } else {
            homePageUrl && history.push(homePageUrl);
        }
    };

    if (!languages || languages.length <= 1) {
        return null;
    }
    const regionWiseData: { [region: string]: LanguageModel[] } = {};

    for (const lang of languages) {
        const region = lang.properties.region || "Unknown";
        if (!regionWiseData[region]) {
            regionWiseData[region] = [];
        }
        regionWiseData[region].push(lang);
    }
    const [isOpenLanguage, setIsOpenLanguage] = useState(false);
    const toggleDropdown = () => setIsOpenLanguage(prev => !prev);
    const handleSelect = (languageId: string) => {
        setLanguage({ languageId });
        setIsOpenLanguage(false);
    };
    const menuId = "languageMenu";
    const selectedLanguage = languages.find(l => l.id === currentLanguage?.id);

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;
    const { width } = useWindowSize();

    return (
        <>
            <Button
                className="menu-trigger-button-hamburger"
                onClick={isOpen ? closeDrawerHamburgerMenu : openDrawerHamburgerMenu}
                {...styles.menuTriggerButton}
                data-test-selector="expandMobileHamburgerMenu"
            >
                <Icon className="menu-trigger-button-icon" src={isOpen ? CrossIcon : Menu} />
                <VisuallyHidden>{translate(isOpen ? "Close menu" : "Menu")}</VisuallyHidden>
            </Button>

            <DrawerHamburger
                className="drawerHamburger"
                draggable
                position="right"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawerHamburgerMenu}
                contentLabel="menu drawerHamburger"
                data-test-selector="drawerSectionHamburger"
                rootElementSelector={rootElementSelector}
            >
                <div>
                    {width !== null &&
                        width <= 992 &&
                        userRoleType !== "ERFQ" &&
                        userName &&
                        !isGuest &&
                        !isPunchOutSession && (
                            // Customer Number Section start
                            <StyledWrapper className="customer-number-wrappper">
                                <PanelMenu
                                    className="panel-menu"
                                    {...styles.panelMenu}
                                    panelTrigger={
                                        <PanelRow
                                            hasChildren
                                            className="logo-links-row mobilePanel"
                                            {...styles.customerNumberRow}
                                            color="common.accent"
                                        >
                                            <Typography
                                                className="logo-link-title"
                                                {...styles.logoLinks?.typographyProps}
                                            >
                                                {/* <Icon src={MapPin} className="menu-row-icon" {...styles.menuRowIcon} /> */}
                                                {/* {translate("hello")} */}
                                                {fulfillmentLabel}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    layer={0}
                                    closeOverlay={closeDrawerHamburgerMenu}
                                    handleOpenScoreCardModal={handleOpenScoreCardModal}
                                    handleFindMyRep={handleFindMyRep}
                                    handleCoreReturns={handleCoreReturns}
                                    handleDownloadLeadTimes={handleDownloadLeadTimes}
                                    handleDownloadPriceList={handleDownloadPriceList}
                                    handleApiKey={handleApiKey}
                                >
                                    <PanelRow
                                        tabIndex={-1}
                                        className="change-customer-row"
                                        {...styles.changeCustomerRow}
                                    >
                                        <GridContainer
                                            className="change-customer-row-container"
                                            {...styles.changeCustomerRowContainer}
                                        >
                                            <GridItem
                                                className="fulfillment-method-grid-item"
                                                {...styles.fulfillmentMethodGridItem}
                                            >
                                                {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem className="addresses-grid-item" {...styles.addressesGridItem}>
                                                {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem
                                                className="pick-up-address-grid-item"
                                                {...styles.pickUpAddressGridItem}
                                            >
                                                {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem
                                                className="apply-button-grid-item"
                                                {...styles.applyButtonGridItem}
                                            >
                                                {createWidgetElement("Header/AddressDrawerApplyButton", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                        </GridContainer>
                                    </PanelRow>
                                </PanelMenu>
                            </StyledWrapper>
                            // Customer Number Section End
                        )}
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {width !== null && width <= 992 && (
                            <PanelMenu
                                key="mobile-productbrouse"
                                className="panel-menu"
                                closeOverlay={closeDrawerHamburgerMenu}
                                panelTrigger={
                                    <PanelRow
                                        className="main-navigation-row"
                                        isCurrent={false}
                                        hasChildren={true}
                                        allowOpenParentLinks={false}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text text-body-md"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {translate("Browse Products")}
                                        </Typography>
                                    </PanelRow>
                                }
                                layer={0}
                            >
                                {!displayVmiNavigation &&
                                    links.map((link: any, index) => {
                                        if (
                                            !!link.childrenType &&
                                            link.children &&
                                            link.children.filter((o: any) => !o.excludeFromNavigation).length > 0
                                        ) {
                                            const href =
                                                allowOpenParentLinks && link.url && link.url !== currentPageUrl
                                                    ? link.url
                                                    : undefined;

                                            const selectedIcon = (link?.linkIcon ?? "none") as keyof typeof iconMap;
                                            const icon = iconMap[selectedIcon];

                                            return (
                                                <PanelMenu
                                                    className="panel-menu"
                                                    key={index}
                                                    currentUrl={currentPageUrl}
                                                    closeOverlay={closeDrawerHamburgerMenu}
                                                    panelTrigger={
                                                        <PanelRow
                                                            className="main-navigation-row"
                                                            target={link?.openInNewWindow ? "_blank" : "_self"}
                                                            href={href}
                                                            isCurrent={link.url === currentPageUrl}
                                                            hasChildren={true}
                                                            allowOpenParentLinks={allowOpenParentLinks}
                                                            {...styles.mainNavigationRowBrowseProductHam}
                                                        >
                                                            <Icon src={icon} size={24} />
                                                            <Typography
                                                                className="main-navigation-row-text"
                                                                {...styles.mainNavigationRowTypography}
                                                            >
                                                                {link.title}
                                                            </Typography>
                                                        </PanelRow>
                                                    }
                                                    menuItems={link.children}
                                                    maxDepth={link.maxDepth || 3}
                                                    layer={0}
                                                    allowOpenParentLinks={allowOpenParentLinks}
                                                    {...styles.panelMenu}
                                                />
                                            );
                                        }

                                        return (
                                            <PanelRow
                                                key={index}
                                                href={link.url}
                                                target={link.openInNewWindow ? "_blank" : ""}
                                                className="main-navigation-row"
                                                onClick={closeDrawerHamburgerMenu}
                                                {...styles.mainNavigationRow}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        );
                                    })}
                            </PanelMenu>
                        )}
                        {!isEmea && (
                            <PanelMenu
                                key="searchByVehicle"
                                className="panel-menu"
                                currentUrl={currentPageUrl}
                                closeOverlay={closeDrawerHamburgerMenu}
                                {...styles.searchByVehiclePanelMenu}
                                panelTrigger={
                                    <PanelRow
                                        className="main-navigation-row"
                                        isCurrent={false}
                                        hasChildren={true}
                                        allowOpenParentLinks={false}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text text-body-md"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {translate("Search by Vehicle")}
                                        </Typography>
                                    </PanelRow>
                                }
                                layer={0}
                            >
                                <Zone zoneName="GlobalSearchFlterfor" contentId={widgetId}></Zone>
                            </PanelMenu>
                        )}
                    </StyledSection>

                    {/* Check checkAvailability */}
                    {width !== null && width <= 992 && (
                        <>
                            <StyledSection
                                className="check-availability-mobile-header-wrapper"
                                {...styles.checkAvailabilityButtonMobileWrapper}
                            >
                                <Button
                                    {...styles.checkAvailabilityButtonMobile}
                                    onClick={buttonClickHandler}
                                    data-test-selector="common_checkAvailabilityButton"
                                    className="text-body-md"
                                >
                                    {translate("Check Availability")}
                                </Button>
                                <Modal
                                    headline={translate("Check Availability")}
                                    isOpen={availabilityModalIsOpen}
                                    handleClose={modalCloseHandler}
                                    {...styles.checkAvailabilityModalMobile}
                                >
                                    {availabilityModalIsOpen && (
                                        <CheckAvailabilityProductSelector data-test-selector="pna_productSelector_global"></CheckAvailabilityProductSelector>
                                    )}
                                </Modal>
                            </StyledSection>
                            {userName && !isGuest && (
                                <StyledSection
                                    className="quick-order-mobile-header-wrapper"
                                    {...styles.quickOrderMobileButtonWrapper}
                                >
                                    <Clickable
                                        {...styles.quickOrderMobileButton}
                                        href={quickOrderUrl}
                                        onClick={closeDrawerHamburgerMenu}
                                        data-test-selector="common_quickOrderMobileButton"
                                        className="text-body-md"
                                    >
                                        {translate("Quick Order")}
                                    </Clickable>
                                </StyledSection>
                            )}
                        </>
                    )}

                    {/* Check checkAvailability ends */}

                    {headerLinkListLinks.links.length > 0 && (
                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            {headerLinkListLinks.links.map((link, index) => {
                                const mappedLinked = link as MappedLink;

                                if (mappedLinked.excludeFromNavigation === true || !link.title) {
                                    return null;
                                }
                                return (
                                    <PanelRow
                                        className="logo-links-row text-body-md"
                                        key={link.title + String(index)}
                                        {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawerHamburgerMenu}
                                        onKeyPress={(event: React.KeyboardEvent) => {
                                            if (event.key === " " && history && link?.url) {
                                                event.preventDefault();
                                                closeDrawerHamburgerMenu();
                                                history.push(link.url);
                                            }
                                        }}
                                        
                                        href={link.url}
                                        target={headerLinkListLinkFields[index].fields.openInNewWindow ? "_blank" : ""}
                                    >
                                        <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                );
                            })}
                        </StyledSection>
                    )}

                    {/* Navigation Drawer My Account */}
                    {width !== null && width <= 992 && (
                        <StyledSection
                            className="drawer-section-wrapper myaccount"
                            {...styles.drawerSectionWrapperMyAccount}
                        >
                            {userName && !isGuest && !isPunchOutSession ? (
                                <PanelMenu
                                    className="panel-menu"
                                    currentUrl={currentPageUrl}
                                    panelTrigger={
                                        <PanelRow
                                            className="main-navigation-row"
                                            hasChildren
                                            {...styles.mainNavigationRow}
                                        >
                                            <StyledSpan
                                                className="panel-section-wrapper"
                                                {...styles.panelSectionWrapper}
                                            >
                                                <Typography className="user-row-text" {...styles.userRowTypography}>
                                                    {translate("Manage Account")}
                                                </Typography>
                                            </StyledSpan>
                                        </PanelRow>
                                    }
                                    menuItems={menuItemLinks ?? []}
                                    maxDepth={3}
                                    closeOverlay={closeDrawerHamburgerMenu}
                                    handleOpenScoreCardModal={handleOpenScoreCardModal}
                                    handleFindMyRep={handleFindMyRep}
                                    handleCoreReturns={handleCoreReturns}
                                    handleDownloadLeadTimes={handleDownloadLeadTimes}
                                    handleDownloadPriceList={handleDownloadPriceList}
                                    handleApiKey={handleApiKey}
                                    layer={0}
                                    {...styles.panelMenuMyaccount}
                                />
                            ) : isPunchOutSession ? (
                                <PanelRow className="main-navigation-row" {...styles.mainNavigationRow}>
                                    <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                        <Icon
                                            className="punch-out-navigation-row-icon"
                                            {...styles.punchOutNavigationRowIcon}
                                        />
                                        <Typography className="punch-out-user-row-text" {...styles.punchOutUserRowText}>
                                            {userName}
                                        </Typography>
                                    </StyledSpan>
                                </PanelRow>
                            ) : null}
                        </StyledSection>
                    )}

                    {width !== null && width <= 992 && canSeeSupportLink && (
                        <>
                            {(currentLanguage && currentLanguage.properties?.region !== EMEARegion) && (
                                <>
                                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                                        <PanelRow
                                            onClick={closeDrawerHamburgerMenu}
                                            href={supportPage?.url ?? "/customer-operations-case-widget"}
                                            target="_blank"
                                            key="Mobile-support"
                                            className="logo-links-row text-body-md"
                                            {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                        >
                                            <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                                {translate("Support")}
                                            </Typography>
                                        </PanelRow>
                                    </StyledSection>
                                </>
                            )}
                        </>
                    )}
                    {/* Navigation Drawer My Account */}
                    {/* For Sign In and Language Selector */}
                    {width !== null && width <= 992 && (
                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            {userName && !isGuest && !isPunchOutSession ? (
                                <PanelRow className="sign-out-row" onClick={onSignOutHandler} {...styles.signOutRow}>
                                    <Typography className="sign-out-row-text text-body-md" {...styles.signOutRowText}>
                                        {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                    </Typography>
                                </PanelRow>
                            ) : isPunchOutSession ? (
                                <PanelRow className="sign-out-row" onClick={onSignOutHandler} {...styles.signOutRow}>
                                    <Typography className="sign-out-row-text text-body-md" {...styles.signOutRowText}>
                                        {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                    </Typography>
                                </PanelRow>
                            ) : (
                                <PanelMenu
                                    key="searchByVehicle"
                                    className="panel-menu"
                                    currentUrl={currentPageUrl}
                                    closeOverlay={closeDrawerHamburgerMenu}
                                    panelTrigger={
                                        <PanelRow
                                            className="main-navigation-row-hamburgersignin"
                                            isCurrent={false}
                                            hasChildren={true}
                                            allowOpenParentLinks={false}
                                            {...styles.mainNavigationRowHamburger}
                                        >
                                            <Icon
                                                className="main-navigation-row-icon"
                                                {...styles.mainNavigationRowIcon}
                                            />
                                            <Typography
                                                className="main-navigation-row-text text-body-md"
                                                {...styles.mainNavigationRowTypographyHamburgerLogin}
                                            >
                                                {translate("Login | Create Account")}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    layer={0}
                                >
                                    <Clickable
                                        {...styles.loginLinkClickable}
                                        onClick={signInPanelClickHandler}
                                        onKeyPress={signInPanelKeyPressHandler}
                                        href={signInUrl?.url}
                                    >
                                        {showIcon && (
                                            <Icon
                                                className="main-navigation-row-icon"
                                                {...styles.mainNavigationRowIcon}
                                            />
                                        )}
                                        {showLabel && (
                                            <Typography className="textWrapper">
                                                <Typography className="text-body-md">{translate("Login")}</Typography>
                                            </Typography>
                                        )}
                                    </Clickable>
                                    {/* Create Account link */}
                                    <Clickable
                                        {...styles.loginLinkClickable}
                                        onClick={createAccountHandler}
                                        href={createAccountUrl?.url}
                                        data-test-selector="header_createAccount"
                                        className="text-body-md"
                                    >
                                        {showLabel && (
                                            <Typography className="textWrapper">
                                                <Typography className="text-body-md">
                                                    {translate("Create Account")}
                                                </Typography>
                                            </Typography>
                                        )}
                                    </Clickable>
                                </PanelMenu>
                            )}
                        </StyledSection>
                    )}
                    {width !== null && width <= 992 && (
                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            {/* <StyledWrapper className="currency-selector-wrapper" {...styles.currencySelectorWrapper}>
                                <SelectorMenu
                                    options={currencies?.map(c => {
                                        return {
                                            title: c.currencyCode.toUpperCase(),
                                            clickableProps: {
                                                onClick: () => {
                                                    setCurrency({ currencyId: c.id });
                                                    closeDrawerHamburgerMenu();
                                                },
                                            },
                                        };
                                    })}
                                    closeModal={closeDrawerHamburgerMenu}
                                    currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                                    currentOptionIcon={
                                        <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                                    }
                                />
                            </StyledWrapper> */}

                            {/* Language Selector */}
                            <StyledWrapper
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleDropdown();
                                    }
                                }}
                            >
                                <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                                    {translate("Language")}
                                </VisuallyHidden>

                                <PanelMenu
                                    key="searchByVehicle3"
                                    className="panel-menu3"
                                    currentUrl={currentPageUrl}
                                    closeOverlay={closeDrawerHamburgerMenu}
                                    panelTrigger={
                                        <PanelRow
                                            className="language-selector-panel-row"
                                            isCurrent={false}
                                            hasChildren={true}
                                            allowOpenParentLinks={false}
                                            {...styles.languageSelectorMobileRow}
                                        >
                                            <Icon
                                                className="menu-row-icon on-mobile-globe"
                                                src={Globe}
                                                {...styles.menuRowIcon}
                                            />
                                            <Typography
                                                className="main-navigation-row-text text-body-md"
                                                {...styles.mainNavigationRowTypographyLanguage}
                                            >
                                                {selectedLanguage?.properties?.region && selectedLanguage?.description
                                                    ? `${selectedLanguage.properties.region
                                                    } | ${selectedLanguage.description.slice(0, 2)}`
                                                    : translate("Select Language")}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    layer={0}
                                >
                                    <StyledWrapper {...styles.languageSelectorWrapperMobile} tabIndex={-1}>
                                        <Typography className="text-body-lg">
                                            {translate("Region & Language")}
                                        </Typography>

                                        <DropdownMenu role="listbox" aria-labelledby={`${menuId}-label`}>
                                            {Object.entries(regionWiseData).map(([region, langs], idx, array) => (
                                                <li
                                                    key={region}
                                                    style={{
                                                        width: "100%",
                                                        borderBottom:
                                                            idx !== array.length - 1
                                                                ? `1px solid ${themeBackgrounds.lightGray}`
                                                                : "none",
                                                    }}
                                                >
                                                    <Typography>
                                                        {region === NARegion
                                                            ? translate("North America")
                                                            : region === LARegion
                                                                ? translate("Latin America")
                                                                : region}
                                                    </Typography>
                                                    <ul style={{ marginTop: "5px" }}>
                                                        {langs.map(lang => (
                                                            <LanguageOption
                                                                key={lang.id}
                                                                onClick={() => handleSelect(String(lang.id))}
                                                                tabIndex={0}
                                                                role="option"
                                                                aria-selected={selectedLanguage?.id === lang.id}
                                                                selected={selectedLanguage?.id === lang.id}
                                                                onKeyDown={e => {
                                                                    if (e.key === "Enter" || e.key === " ") {
                                                                        e.preventDefault();
                                                                        handleSelect(String(lang.id));
                                                                    }
                                                                }}
                                                                className="text-body-sm"
                                                            >
                                                                {lang.description}
                                                            </LanguageOption>
                                                        ))}
                                                    </ul>
                                                </li>
                                            ))}
                                        </DropdownMenu>
                                    </StyledWrapper>
                                </PanelMenu>
                            </StyledWrapper>
                            {displayModeSwitch && (
                                <SelectorMenu
                                    options={[
                                        {
                                            title: translate("Storefront"),
                                            clickableProps: {
                                                onClick: () => {
                                                    navigationModeChangeHandler("Storefront");
                                                    closeDrawerHamburgerMenu();
                                                },
                                            },
                                        },
                                        {
                                            title: translate("Vendor Managed Inventory"),
                                            clickableProps: {
                                                onClick: () => {
                                                    navigationModeChangeHandler("Vmi");
                                                    closeDrawerHamburgerMenu();
                                                },
                                            },
                                        },
                                    ]}
                                    closeModal={closeDrawerHamburgerMenu}
                                    currentOption={
                                        currentMode === "Vmi"
                                            ? translate("Vendor Managed Inventory")
                                            : translate("Storefront")
                                    }
                                />
                            )}
                            {displayPricingAndInventoryToggles && (
                                <>
                                    {(!isCompactHeaderFlyout || showFlyoutInventoryToggle) && (
                                        <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                                    )}
                                    {(!isCompactHeaderFlyout || showFlyoutPricingToggle) && (
                                        <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                                    )}
                                </>
                            )}
                        </StyledSection>
                    )}
                </div>
            </DrawerHamburger>

            <Modal headline={scoreCardModalTitle} isOpen={openScoreCardModal}
                handleClose={closeScoreCardModal}
                {...styles.modal}
            >
                {isScorecardLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                ) : (
                    <CustomerScoreCard url={scorecardUrl} />
                )}
            </Modal>
            <Modal
                headline={translate("Logging into Core Returns")}
                isOpen={openCoreReturnstModal}
                isCloseable={false}
                {...styles.modal}
            >
                <>
                    {isCoreReturnsLoading ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <></>
                    )}
                    <form ref={coreReturnsFormRef} action={coreReturnsData?.url} method="post" {...styles.form}>
                        <input type="hidden" id="email" name="email" value={coreReturnsData?.email} />
                        <input type="hidden" id="token" name="token" value={coreReturnsData?.token} />
                        <input type="hidden" id="dealerCode" name="dealerCode" value={coreReturnsData?.dealerCode} />
                        <input type="hidden" id="clientId" name="clientId" value={coreReturnsData?.clientId} />
                        <input
                            type="hidden"
                            id="clientSecret"
                            name="clientSecret"
                            value={coreReturnsData?.clientSecret}
                        />
                    </form>
                </>
            </Modal>
            <Modal
                headline={translate("Logging into Co-op")}
                isOpen={openCoopModal}
                isCloseable={false}
                {...styles.modal}
            >
                <>
                    {isCoopLoading ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <></>
                    )}
                    <form ref={coopFormRef} action={coopData?.url} method="post" {...styles.form}>
                        <input type="hidden" id="accessToken" name="accessToken" value={coopData?.accessToken} />
                        <input
                            type="hidden"
                            id="custom_sso_domain_name"
                            name="custom_sso_domain_name"
                            value={url?.hostname}
                        />
                        <input type="hidden" id="email" name="email" value={coopData?.email} />
                        <input type="hidden" id="first_name" name="first_name" value={coopData?.first_name} />
                        <input type="hidden" id="last_name" name="last_name" value={coopData?.last_name} />
                    </form>
                </>
            </Modal>
            <Modal
                headline={translate("My Sales Representative")}
                isOpen={openSalesRepModal}
                handleClose={closeSalesRepModal}
            >
                <StyledWrapper {...styles.salesRepwrapper}>
                    {/* If part */}
                    {salesRep !== undefined && salesRep?.email !== null && (
                        <StyledWrapper {...styles.salesRepDetails}>
                            <Typography {...styles.salesRepName}>{salesRep?.name}</Typography>
                            <Link href={`mailto:${salesRep?.email}`} {...styles.salesRepEmail}>
                                {salesRep?.email}
                            </Link>
                            <Link href={`tel:${salesRep?.phone}`} {...styles.salesRepContact}>
                                {salesRep?.phone}
                            </Link>
                        </StyledWrapper>
                    )}
                    {/* End If part */}

                    {/* Elase Part */}
                    {(salesRep === undefined || salesRep?.email === null) && (<div>
                        <Typography>{siteMessage("ProductDetailPage_SalesRepNotAssigned_Message")}</Typography>
                    </div>
                    )}
                    {/* End Else part */}
                </StyledWrapper>
            </Modal>
            <Modal
                headline={translate("Downloading lead times")}
                isOpen={openLeadTimesModal}
                isCloseable={false}
                {...styles.modal}
            >
                <>
                    {isLeadTimesLoading ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <></>
                    )}
                </>
            </Modal>
            <Modal
                headline={translate("Download Price List")}
                isOpen={openPriceListModal}
                isCloseable={!isPriceListLoading}
                {...styles.modal}
                handleClose={handleCloseDownloadPriceList}
            >
                {isPriceListLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                ) : (
                    <>
                        <Typography variant="h5">{translate("Terms and Conditions:")}</Typography>
                        <Typography variant="p">
                            {translate(
                                "Dana's Aftermarket group publishes net price and takes every effort to assure its accuracy. However, emergency order premiums, surcharges, tariffs, freight, and other charges may apply to final pricing. Your placement of an order, is based on final pricing applied at the time of order your entry and is considered your acceptance and agreement of Dana's final pricing. Availability or advertising of your net pricing does not constitute a firm offer or commitment to sell at that price. Pricing is subject to change without notice to the customer. Pricing is Dana confidential information and is intended for the sole use of the customer. Use of this file by unauthorized persons is strictly prohibited. Publication, distribution, dissemination, or sharing of this file is strictly prohibited under penalty of law.",
                            )}
                        </Typography>
                        <Button
                            title={translate("Confirm")}
                            onClick={() => {
                                setIsPriceListLoading(true);
                                toasterContext.addToast({
                                    body: translate("Price List download has started."),
                                    messageType: "success",
                                    isFocusAfterMount: true,
                                });
                                downloadPriceList(isFuture).then(requestid => {
                                    if (requestid) {
                                        setPriceListRequestId(requestid);
                                    }
                                });
                            }}
                        >
                            {translate("Confirm")}
                        </Button>
                    </>
                )}
            </Modal>
            <Modal
                {...styles.modal}
                handleClose={closeApiKeyModalHandler}
                headline={translate("Developer Portal API Key")}
                isOpen={isApiKeyOpen}
                data-test-selector="apiKeyModal"
            >
                {isLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                ) : errorMessage === undefined ? (
                    <GridContainer {...styles.container}>
                        {hasGenerated ? (
                            <GridItem {...styles.modalGridItem}>
                                <Typography {...styles.h2}>{translate("API Key")}:</Typography>
                                <Typography {...styles.key}>{apiKey}</Typography>
                            </GridItem>
                        ) : (
                            <GridItem {...styles.modalGridItem}>
                                <Button
                                    {...styles.generateButton}
                                    data-test-selector="createUserModal_createUserButton"
                                    type="submit"
                                    onClick={generateKey}
                                >
                                    {translate("Generate API Key")}
                                </Button>
                            </GridItem>
                        )}
                    </GridContainer>
                ) : (
                    <GridItem {...styles.modalGridItem}>
                        <Typography {...styles.h2}>{translate("Error")}:</Typography>
                        <Typography {...styles.key}>{errorMessage}</Typography>
                    </GridItem>
                )}
            </Modal>
        </>
    );
};

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="logo-links-row mobilePanel" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title mobile-logo-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawerHamburgerMenu)));
